import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Router, ActivatedRoute, Params } from '@angular/router';

import { ISnListQueryParameters, ISnListResponse } from '@lib/api';
import { SnCamera, SnOperationalView, SnWidget } from '@lib/api/entities';
import { SnOperationalViewsAPI } from '@lib/api/services';
import { SnDataListComponent } from '@lib/ui/data-list';

@Component({
  selector: 'sn-view-list-dialog',
  templateUrl: './view-list-dialog.component.html',
  styleUrls: ['./view-list-dialog.component.scss'],
})
export class ViewListDialogComponent implements OnInit, OnDestroy {
  @ViewChild('list', { static: false })
  public readonly list: SnDataListComponent<SnOperationalView>;
  public readonly loadData = this._loadData.bind(this);
  public readonly columns: string[] = ['name'];

  private readonly _destroy$ = new Subject<void>();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: SnCamera,
    private readonly _dialogRef: MatDialogRef<ViewListDialogComponent>,
    private readonly _operationalViewsAPI: SnOperationalViewsAPI,
    private readonly _router: Router,
    private readonly _activatedRoute: ActivatedRoute,
  ) {}

  public ngOnInit(): void {
    this._onInit();
  }

  public ngOnDestroy(): void {
    this._router.navigate([], { queryParams: {} });
    this._destroy$.next();
    this._destroy$.complete();
  }

  public onSearchValueChange(value: string): void {
    this._updateQueryParams({ search: value });
  }

  public onSelected(view: SnOperationalView): void {
    this._operationalViewsAPI
      .createViewWidget(view.id, {
        widget: 'camera_live_view',
        name: 'Camera Live View',
        width: 2,
        height: 4,
        col: null,
        row: null,
      })
      .pipe(takeUntil(this._destroy$))
      .subscribe((widget) => {
        this._saveCamera(widget);
      });
  }
  public cancel(): void {
    this._dialogRef.close();
  }

  private _saveCamera(widget: SnWidget): void {
    this._operationalViewsAPI
      .updateViewWidget(widget.operationalView, widget.id, {
        widgetObjects: [
          {
            contentType: 'camera',
            objectId: this.data.id,
          },
        ],
      })
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => this._dialogRef.close());
  }

  private _onInit(): void {
    this._router.navigate([], { queryParams: {} });
    setTimeout(() => {
      this.list.reference.reload();
    }, 500);
  }

  private _updateQueryParams(params: Params): void {
    this._router.navigate([], {
      relativeTo: this._activatedRoute,
      queryParamsHandling: 'merge',
      queryParams: params,
    });
  }

  private _loadData(
    params: ISnListQueryParameters,
  ): Observable<ISnListResponse<SnOperationalView>> {
    return this._operationalViewsAPI.getViewList(this._prepareQueryParams(params));
  }

  private _prepareQueryParams<T extends ISnListQueryParameters>(params: T): T {
    return {
      ordering: {
        active: 'name',
        direction: 'asc',
      },
      ...params,
    };
  }
}
